import React from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import DashboardShell from "@/components/dashboard/DashboardShell";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  Plus, Clock, CheckCircle2, FileText, MapPin, Calendar, Tag,
  AlertCircle, Loader2, X, Truck, BadgeCheck, Lock, ShieldCheck, MailCheck, Star,
  ArrowRight, Sparkles, Route, PoundSterling,
} from "lucide-react";
import RatingModal from "@/components/RatingModal";
import PlaceAutocompleteInput from "@/components/PlaceAutocompleteInput";
import JobTimerPanel from "@/components/JobTimerPanel";
import SavedPaymentMethodsPanel from "@/components/customer/SavedPaymentMethodsPanel";
import DisputesSection from "@/components/disputes/DisputesSection";

const STATUS_META = {
  open: { label: "Open · awaiting quotes", cls: "bg-[#F5A623]/10 text-[#A66800] border-[#F5A623]/40" },
  quoted: { label: "Quotes in", cls: "bg-[#1E4D55]/10 text-[#1E4D55] border-[#1E4D55]/40" },
  priced: { label: "Priced · pay to publish", cls: "bg-[#F5A623]/10 text-[#A66800] border-[#F5A623]/40" },
  awaiting_driver: { label: "Live · awaiting driver", cls: "bg-[#1E4D55]/10 text-[#1E4D55] border-[#1E4D55]/40" },
  accepted: { label: "Accepted · pay to confirm", cls: "bg-[#F5A623]/10 text-[#A66800] border-[#F5A623]/40" },
  paid: { label: "Paid · driver to start", cls: "bg-[#1E4D55]/10 text-[#1E4D55] border-[#1E4D55]/40" },
  in_progress: { label: "In progress", cls: "bg-[#1E4D55]/10 text-[#1E4D55] border-[#1E4D55]/40" },
  ready_for_completion: { label: "Ready · confirm to release funds", cls: "bg-[#F5A623]/10 text-[#A66800] border-[#F5A623]/40" },
  completed: { label: "Completed", cls: "bg-[#5BB247]/10 text-[#2c8642] border-[#5BB247]/40" },
  cancelled: { label: "Cancelled", cls: "bg-[#B91C1C]/10 text-[#B91C1C] border-[#FCA5A5]" },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.open;
  return (
    <span className={`inline-flex items-center gap-1 border px-2 py-1 text-[11px] uppercase tracking-[0.14em] font-bold ${m.cls}`}>
      {m.label}
    </span>
  );
}

function gbp(p) {
  if (p == null) return "—";
  return `£${(p / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [jobs, setJobs] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [showCreate, setShowCreate] = React.useState(false);
  const [openJobId, setOpenJobId] = React.useState(null);
  // After auto-pricing a job, we open the checkout modal at the dashboard level
  // so the customer pays upfront in one continuous motion.
  const [autoPayJobId, setAutoPayJobId] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/customer/jobs");
      setJobs(data.jobs || []);
      setCategories(data.categories || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const stats = React.useMemo(() => {
    const active = jobs.filter((j) => ["open", "quoted", "priced", "awaiting_driver", "accepted", "paid", "in_progress", "ready_for_completion"].includes(j.status)).length;
    const completed = jobs.filter((j) => j.status === "completed").length;
    const inEscrow = 0; // wired in next session when escrow lands
    return { active, completed, inEscrow };
  }, [jobs]);

  return (
    <DashboardShell eyebrow="Customer" title={`Hi ${user?.name?.split(" ")[0] || "there"}.`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-[#0A0A0A]/10 mb-10">
        {[
          { label: "Active jobs", value: stats.active, Icon: Clock },
          { label: "Completed", value: stats.completed, Icon: CheckCircle2 },
          { label: "In escrow", value: gbp(stats.inEscrow * 100), Icon: FileText },
        ].map((s) => {
          const I = s.Icon;
          return (
            <div key={s.label} data-testid={`customer-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white border-r border-b border-[#0A0A0A]/10 p-6">
              <div className="flex items-start justify-between">
                <I size={18} className="text-[#1E4D55]" />
                <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#475569]">{s.label}</span>
              </div>
              <p className="mt-6 font-display text-4xl font-black tracking-tighter text-[#0A0A0A]">{s.value}</p>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A]">Your jobs</h2>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          data-testid="customer-new-job-button"
          className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white transition-colors px-5 py-3 font-semibold"
        >
          <Plus size={16} />
          <span>Post a job</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div data-testid="customer-jobs-loading" className="text-[#475569] text-sm inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : jobs.length === 0 ? (
        <div data-testid="customer-empty-state" className="bg-white border border-[#0A0A0A]/10 border-dashed p-10 text-center">
          <p className="font-display text-xl font-bold text-[#0A0A0A]">No jobs yet.</p>
          <p className="mt-2 text-[#475569] max-w-md mx-auto">
            Post your first job — house removal, waste clearance, office move — and verified Luton van drivers will quote you within minutes.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="customer-job-list">
          {jobs.map((j) => (
            <button
              key={j.id}
              onClick={() => setOpenJobId(j.id)}
              data-testid={`customer-job-row-${j.id}`}
              className="w-full text-left bg-white border border-[#0A0A0A]/10 hover:border-[#1E4D55] transition-colors p-5 flex flex-wrap items-start gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#1E4D55]">
                    {j.category_label}
                  </span>
                  <StatusPill status={j.status} />
                </div>
                <p className="mt-2 font-display text-xl font-bold tracking-tight text-[#0A0A0A]">{j.title}</p>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#475569]">
                  <span className="inline-flex items-center gap-1"><MapPin size={12} /> {j.pickup_address}{j.dropoff_address ? ` → ${j.dropoff_address}` : ""}</span>
                  {j.scheduled_date && <span className="inline-flex items-center gap-1"><Calendar size={12} /> {j.scheduled_date}</span>}
                </div>
              </div>
              <div className="text-right">
                <p className="font-display text-2xl font-black tracking-tighter text-[#0A0A0A]">
                  {j.quote_count}
                </p>
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#475569]">
                  quote{j.quote_count === 1 ? "" : "s"}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateJobModal
          categories={categories}
          onClose={() => setShowCreate(false)}
          onCreated={async (createdJob) => {
            setShowCreate(false);
            await load();
            // v18 — if the job was auto-priced, immediately prompt for payment
            if (createdJob && createdJob.status === "priced") {
              setAutoPayJobId(createdJob.id);
            }
          }}
        />
      )}

      {/* v23.13 — Saved payment methods for auto-charging the 70% balance off-session */}
      <SavedPaymentMethodsPanel />

      {/* v23.14 — Disputes */}
      <DisputesSection role="customer" />

      {autoPayJobId && (
        <CheckoutModal
          jobId={autoPayJobId}
          onClose={() => setAutoPayJobId(null)}
          onPaid={async () => {
            setAutoPayJobId(null);
            await load();
          }}
        />
      )}

      {openJobId && (
        <JobDetailModal
          jobId={openJobId}
          onClose={() => setOpenJobId(null)}
          onChanged={load}
        />
      )}
    </DashboardShell>
  );
}

function CreateJobModal({ categories, onClose, onCreated }) {
  const [step, setStep] = React.useState(1);
  const [vanClasses, setVanClasses] = React.useState([]);
  const [form, setForm] = React.useState({
    category: "house_removal",
    title: "",
    pickup_address: "",
    dropoff_address: "",
    scheduled_date: "",
    description: "",
    van_class: "luton",
    estimated_hours: "",
    pickup_floors: 0,
    dropoff_floors: 0,
    extra_stops: 0,
    congestion_zone: false,
    extra_helper: false,
  });
  // v21 — Promo code state
  const [promoCode, setPromoCode] = React.useState("");
  const [promo, setPromo] = React.useState(null); // {discount_pence, final_pence}
  const [promoError, setPromoError] = React.useState("");
  const [promoChecking, setPromoChecking] = React.useState(false);
  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoChecking(true);
    setPromoError("");
    try {
      const { data } = await apiClient.post("/promo-codes/validate", {
        code: promoCode.trim().toUpperCase(),
        amount_pence: estimate?.total_pence || 0,
      });
      setPromo(data);
    } catch (e) {
      setPromo(null);
      setPromoError(formatApiErrorDetail(e.response?.data?.detail) || "Invalid code");
    } finally {
      setPromoChecking(false);
    }
  };
  const [estimate, setEstimate] = React.useState(null);
  const [estimating, setEstimating] = React.useState(false);
  const [estimateError, setEstimateError] = React.useState("");
  const [error, setError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const update = (f) => (e) => setForm((s) => ({ ...s, [f]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const setField = (f, v) => setForm((s) => ({ ...s, [f]: v }));
  const selectedCat = categories.find((c) => c.key === form.category);

  // Load van class catalogue once
  React.useEffect(() => {
    apiClient.get("/pricing/van-classes")
      .then(({ data }) => setVanClasses(data.van_classes || []))
      .catch(() => setVanClasses([]));
  }, []);

  // Debounced live estimate whenever step 2 inputs change
  React.useEffect(() => {
    if (step !== 2) return;
    if (!form.pickup_address || !form.dropoff_address || !form.van_class) return;
    let cancelled = false;
    setEstimating(true);
    setEstimateError("");
    const t = setTimeout(async () => {
      try {
        const { data } = await apiClient.post("/pricing/estimate", {
          pickup_address: form.pickup_address,
          dropoff_address: form.dropoff_address,
          van_class: form.van_class,
          estimated_hours: form.estimated_hours ? parseFloat(form.estimated_hours) : undefined,
          pickup_floors: parseInt(form.pickup_floors) || 0,
          dropoff_floors: parseInt(form.dropoff_floors) || 0,
          extra_stops: parseInt(form.extra_stops) || 0,
          congestion_zone: !!form.congestion_zone,
          extra_helper: !!form.extra_helper,
        });
        if (!cancelled) setEstimate(data);
      } catch (e) {
        if (!cancelled) {
          setEstimate(null);
          setEstimateError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't price this route");
        }
      } finally {
        if (!cancelled) setEstimating(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [step, form.pickup_address, form.dropoff_address, form.van_class, form.estimated_hours,
      form.pickup_floors, form.dropoff_floors, form.extra_stops, form.congestion_zone, form.extra_helper]);

  const goToStep2 = (e) => {
    e.preventDefault();
    setError("");
    if (!form.pickup_address.trim() || !form.dropoff_address.trim()) {
      setError("Both pickup and drop-off addresses are required for an instant price.");
      return;
    }
    if (form.title.trim().length < 4) { setError("Job title must be at least 4 characters."); return; }
    if (form.description.trim().length < 8) { setError("Add a few details about the move (at least 8 characters)."); return; }
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!estimate) {
      setError("Wait for the price to load before posting.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const created = await apiClient.post("/customer/jobs", {
        category: form.category,
        title: form.title.trim(),
        pickup_address: form.pickup_address.trim(),
        dropoff_address: form.dropoff_address.trim(),
        scheduled_date: form.scheduled_date || null,
        description: form.description.trim(),
        van_class: form.van_class,
        estimated_hours: form.estimated_hours ? parseFloat(form.estimated_hours) : undefined,
        pickup_floors: parseInt(form.pickup_floors) || 0,
        dropoff_floors: parseInt(form.dropoff_floors) || 0,
        extra_stops: parseInt(form.extra_stops) || 0,
        congestion_zone: !!form.congestion_zone,
        extra_helper: !!form.extra_helper,
        promo_code: promo ? promo.code : null,
      });
      // Bubble the freshly-created job back so parent can immediately open
      // the payment modal (status === "priced" → upfront escrow).
      await onCreated(created.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to post job");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="create-job-modal" className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white border border-[#0A0A0A]/10 max-w-2xl w-full p-8 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
              {step === 1 ? "Step 1 of 2 · Job details" : "Step 2 of 2 · Get instant price"}
            </p>
            <h3 className="font-display mt-2 text-2xl font-bold tracking-tight text-[#0A0A0A]">
              {step === 1 ? "Tell us what needs moving." : "Pick a van. See the price."}
            </h3>
          </div>
          <button onClick={onClose} data-testid="create-job-close" className="p-2 hover:bg-[#F5F5F5]"><X size={18} /></button>
        </div>

        {step === 1 && (
          <form onSubmit={goToStep2} className="mt-6 space-y-4" data-testid="create-job-form">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">Type</label>
              <div role="tablist" className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {categories.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    role="tab"
                    aria-selected={form.category === c.key}
                    onClick={() => setField("category", c.key)}
                    data-testid={`create-job-category-${c.key}`}
                    className={`px-3 py-2.5 text-xs font-semibold tracking-tight border transition-colors ${
                      form.category === c.key
                        ? "bg-[#1E4D55] text-white border-[#1E4D55]"
                        : "bg-white text-[#0A0A0A] border-[#0A0A0A]/15 hover:border-[#1E4D55]/40"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              {selectedCat?.requires_doc_type && (
                <p className="mt-2 text-[11px] text-[#A66800]">· requires waste carrier–licensed drivers</p>
              )}
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">Title</label>
              <input
                type="text" required minLength={4} maxLength={120}
                value={form.title} onChange={update("title")}
                placeholder="e.g. 2-bed flat move Leeds → Manchester"
                data-testid="create-job-title"
                className="w-full px-3 py-2.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">Pickup address</label>
                <PlaceAutocompleteInput
                  value={form.pickup_address}
                  onChange={(v) => setField("pickup_address", v)}
                  placeholder="Start typing… e.g. SE1 2AB"
                  required
                  testId="create-job-pickup"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">Drop-off address</label>
                <PlaceAutocompleteInput
                  value={form.dropoff_address}
                  onChange={(v) => setField("dropoff_address", v)}
                  placeholder="Start typing destination postcode"
                  required
                  testId="create-job-dropoff"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">Preferred date (optional)</label>
              <input
                type="date" value={form.scheduled_date} onChange={update("scheduled_date")}
                data-testid="create-job-date"
                className="w-full px-3 py-2.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">Details</label>
              <textarea
                required minLength={8} maxLength={2000} rows={3}
                value={form.description} onChange={update("description")}
                placeholder="Rooms, items, floors, parking, access. The more detail, the smoother the move."
                data-testid="create-job-description"
                className="w-full px-3 py-2.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
              />
            </div>

            {error && (
              <div data-testid="create-job-error" className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} data-testid="create-job-cancel" className="px-5 py-3 text-sm font-semibold text-[#475569] hover:text-[#0A0A0A]">Cancel</button>
              <button type="submit" data-testid="create-job-next" className="inline-flex items-center gap-2 bg-[#1E4D55] text-white hover:bg-[#163C42] transition-colors px-5 py-3 font-semibold">
                <span>See instant price</span>
                <ArrowRight size={16} />
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <div className="mt-6 space-y-5" data-testid="create-job-pricing-step">
            {/* Van class picker */}
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">Pick a van class</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {vanClasses.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => setField("van_class", v.key)}
                    data-testid={`van-class-${v.key}`}
                    className={`text-left p-3 border transition-colors ${
                      form.van_class === v.key
                        ? "bg-[#1E4D55] text-white border-[#1E4D55]"
                        : "bg-white text-[#0A0A0A] border-[#0A0A0A]/15 hover:border-[#1E4D55]/40"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-sm">{v.label}</span>
                      <Truck size={14} />
                    </div>
                    <p className={`mt-1 text-[11px] ${form.van_class === v.key ? "text-white/80" : "text-[#475569]"}`}>
                      {v.capacity_blurb}
                    </p>
                    <p className={`mt-1 text-[11px] font-mono ${form.van_class === v.key ? "text-white/80" : "text-[#475569]"}`}>
                      £{(v.base_hr_pence/100).toFixed(2)}/hr · £{(v.per_mile_pence/100).toFixed(2)}/mi
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Adjustments */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569] mb-1">Hours</label>
                <input
                  type="number" min="2" max="24" step="0.5"
                  value={form.estimated_hours}
                  onChange={update("estimated_hours")}
                  placeholder="auto"
                  data-testid="create-job-hours"
                  className="w-full px-2 py-2 bg-white border border-[#0A0A0A]/15 text-sm focus:outline-none focus:border-[#1E4D55]"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569] mb-1">Pickup floor</label>
                <input
                  type="number" min="0" max="20" value={form.pickup_floors}
                  onChange={update("pickup_floors")}
                  data-testid="create-job-pickup-floors"
                  className="w-full px-2 py-2 bg-white border border-[#0A0A0A]/15 text-sm focus:outline-none focus:border-[#1E4D55]"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569] mb-1">Dropoff floor</label>
                <input
                  type="number" min="0" max="20" value={form.dropoff_floors}
                  onChange={update("dropoff_floors")}
                  data-testid="create-job-dropoff-floors"
                  className="w-full px-2 py-2 bg-white border border-[#0A0A0A]/15 text-sm focus:outline-none focus:border-[#1E4D55]"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569] mb-1">Extra stops</label>
                <input
                  type="number" min="0" max="10" value={form.extra_stops}
                  onChange={update("extra_stops")}
                  data-testid="create-job-extra-stops"
                  className="w-full px-2 py-2 bg-white border border-[#0A0A0A]/15 text-sm focus:outline-none focus:border-[#1E4D55]"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.congestion_zone} onChange={update("congestion_zone")} data-testid="create-job-ccz" />
                <span className="text-[#0A0A0A]">London CCZ (+£18)</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.extra_helper} onChange={update("extra_helper")} data-testid="create-job-extra-helper" />
                <span className="text-[#0A0A0A]">Add a helper</span>
              </label>
            </div>

            {/* Live estimate panel */}
            <div data-testid="pricing-panel" className="bg-[#F5F5F5] border border-[#0A0A0A]/10 p-5">
              {estimating && (
                <div className="inline-flex items-center gap-2 text-sm text-[#475569]">
                  <Loader2 size={14} className="animate-spin" /> Calculating route & price…
                </div>
              )}
              {!estimating && estimateError && (
                <div className="flex items-start gap-2 text-sm text-[#B91C1C]">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{estimateError}</span>
                </div>
              )}
              {!estimating && estimate && (
                <>
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">Your fixed price</p>
                      <p className="mt-1 font-display text-4xl font-black tracking-tighter text-[#0A0A0A]" data-testid="pricing-total">
                        {gbp(estimate.total_pence)}
                      </p>
                    </div>
                    <div className="text-right text-xs text-[#475569] space-y-0.5">
                      <p className="inline-flex items-center gap-1"><Route size={12} /> {estimate.distance_mi} mi · {Math.round(estimate.drive_time_min)} min drive</p>
                      <p className="inline-flex items-center gap-1"><Clock size={12} /> {estimate.estimated_hours}h booked · {estimate.men_count}-man crew</p>
                    </div>
                  </div>
                  <div className="mt-4 space-y-1 text-xs text-[#475569]" data-testid="pricing-breakdown">
                    {estimate.breakdown.map((b, i) => (
                      <div key={i} className="flex items-center justify-between gap-3">
                        <span>{b.label}</span>
                        <span className="font-mono text-[#0A0A0A]">{gbp(b.amount_pence)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#0A0A0A]/10 text-[11px] text-[#475569] flex items-start gap-1.5">
                    <Sparkles size={11} className="mt-0.5 text-[#5BB247]" />
                    <span>
                      Overtime (if the job runs past {estimate.estimated_hours}h) is paid in CASH directly to the driver at <strong>£{(estimate.ot_pence_per_30/100).toFixed(2)}/30min</strong>. Raanah doesn&apos;t take a cut on overtime.
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* v21 — Promo code input */}
            {estimate && (
              <div data-testid="promo-code-row" className="bg-white border border-[#0A0A0A]/10 p-4">
                <label className="block text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569] mb-2">Promo code (optional)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromo(null); setPromoError(""); }}
                    placeholder="e.g. WELCOME20"
                    disabled={!!promo}
                    data-testid="promo-code-input"
                    className="flex-1 px-3 py-2 border border-[#0A0A0A]/15 font-mono uppercase focus:border-[#1E4D55] outline-none text-sm disabled:opacity-60"
                  />
                  {!promo ? (
                    <button
                      type="button"
                      onClick={validatePromo}
                      disabled={!promoCode.trim() || promoChecking}
                      data-testid="promo-code-apply"
                      className="px-4 py-2 bg-[#1E4D55] hover:bg-[#163C42] text-white text-xs font-semibold disabled:opacity-60"
                    >
                      {promoChecking ? "Checking…" : "Apply"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setPromo(null); setPromoCode(""); }}
                      data-testid="promo-code-remove"
                      className="px-3 py-2 text-xs font-semibold text-[#B91C1C] hover:underline"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {promo && (
                  <p data-testid="promo-code-success" className="mt-2 text-xs text-[#2c8642] inline-flex items-center gap-1">
                    ✓ <strong>{promo.code}</strong> applied — you save {gbp(promo.discount_pence)}. New total: <strong>{gbp(promo.final_pence)}</strong>
                  </p>
                )}
                {promoError && <p data-testid="promo-code-error" className="mt-2 text-xs text-[#B91C1C]">{promoError}</p>}
              </div>
            )}

            {error && (
              <div data-testid="create-job-error" className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-2">
              <button type="button" onClick={() => setStep(1)} data-testid="create-job-back" className="px-3 py-3 text-sm font-semibold text-[#475569] hover:text-[#0A0A0A]">
                ← Back
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !estimate || estimating}
                data-testid="create-job-submit"
                className="inline-flex items-center gap-2 bg-[#1E4D55] text-white hover:bg-[#163C42] transition-colors px-5 py-3 font-semibold disabled:opacity-60"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                <span>{submitting ? "Posting…" : (estimate ? `Pay ${gbp(promo?.final_pence ?? estimate.total_pence)} & post` : "Post job")}</span>
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function JobDetailModal({ jobId, onClose, onChanged }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [acting, setActing] = React.useState(false);
  const [showCheckout, setShowCheckout] = React.useState(false);
  const [showBalanceCheckout, setShowBalanceCheckout] = React.useState(false);
  const [showRating, setShowRating] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/customer/jobs/${jobId}`);
      setData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  React.useEffect(() => { load(); }, [load]);

  const acceptQuote = async (quoteId) => {
    setActing(true);
    try {
      await apiClient.post(`/customer/jobs/${jobId}/accept-quote/${quoteId}`);
      await load();
      if (onChanged) await onChanged();
      // Immediately prompt for payment to lock the commitment
      setShowCheckout(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to accept");
    } finally {
      setActing(false);
    }
  };

  const confirmComplete = async () => {
    if (!window.confirm("Confirm the job was completed to your satisfaction? This releases funds to the driver.")) return;
    setActing(true);
    try {
      await apiClient.post(`/customer/jobs/${jobId}/confirm-complete`);
      await load();
      if (onChanged) await onChanged();
      // Prompt for rating right after funds are released
      setShowRating(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to confirm");
    } finally {
      setActing(false);
    }
  };

  const cancelJob = async () => {
    if (!window.confirm("Cancel this job? Any escrow funds will be fully refunded.")) return;
    setActing(true);
    try {
      await apiClient.delete(`/customer/jobs/${jobId}`);
      onClose();
      if (onChanged) await onChanged();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to cancel");
      setActing(false);
    }
  };

  return (
    <div data-testid="job-detail-modal" className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white border border-[#0A0A0A]/10 max-w-2xl w-full p-8 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
              {data?.job?.category_label || "Job"}
            </p>
            <h3 className="font-display mt-2 text-2xl font-bold tracking-tight text-[#0A0A0A]">
              {data?.job?.title || "Loading…"}
            </h3>
          </div>
          <button onClick={onClose} data-testid="job-detail-close" className="p-2 hover:bg-[#F5F5F5]"><X size={18} /></button>
        </div>

        {loading && <div className="mt-6 text-[#475569] text-sm inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}

        {error && <div className="mt-4 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3"><AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span></div>}

        {data?.job && (
          <>
            <div className="mt-5 flex items-center gap-2 flex-wrap">
              <StatusPill status={data.job.status} />
              {data.job.scheduled_date && (
                <span className="text-xs text-[#475569] inline-flex items-center gap-1"><Calendar size={12} /> {data.job.scheduled_date}</span>
              )}
              {data.job.budget_pence != null && (
                <span className="text-xs text-[#475569] inline-flex items-center gap-1"><Tag size={12} /> Budget hint: {gbp(data.job.budget_pence)}</span>
              )}
            </div>

            <div className="mt-4 text-sm text-[#0A0A0A]">
              <p className="inline-flex items-start gap-2"><MapPin size={14} className="mt-0.5 text-[#1E4D55]" /><span>{data.job.pickup_address}{data.job.dropoff_address ? ` → ${data.job.dropoff_address}` : ""}</span></p>
              <p className="mt-3 text-[#475569] whitespace-pre-wrap">{data.job.description}</p>
            </div>

            <h4 className="mt-8 font-display text-lg font-bold tracking-tight text-[#0A0A0A]">
              Quotes ({data.quotes?.length || 0})
            </h4>

            {(!data.quotes || data.quotes.length === 0) ? (
              <p className="mt-3 text-sm text-[#475569]">No quotes yet — verified drivers will start submitting shortly.</p>
            ) : (
              <div className="mt-4 space-y-3" data-testid="job-quotes-list">
                {data.quotes.map((q) => (
                  <div key={q.id} data-testid={`job-quote-${q.id}`} className={`border p-4 ${q.status === "accepted" ? "border-[#5BB247]/60 bg-[#5BB247]/5" : "border-[#0A0A0A]/10 bg-white"}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-semibold text-[#0A0A0A] inline-flex items-center gap-2">
                          {q.driver?.name || "Driver"}
                          {q.driver?.verified && <BadgeCheck size={14} className="text-[#5BB247]" />}
                          {q.driver?.rating_count > 0 && (
                            <span data-testid={`driver-rating-${q.id}`} className="inline-flex items-center gap-0.5 text-xs text-[#A66800] font-bold">
                              <Star size={12} fill="#F5A623" className="text-[#F5A623]" />
                              {q.driver.rating_avg?.toFixed(1)}
                              <span className="text-[#475569] font-normal">({q.driver.rating_count})</span>
                            </span>
                          )}
                        </p>
                        {/* Trust badges (v15+) */}
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`job-quote-trust-${q.id}`}>
                          {q.driver?.email_verified && (
                            <span data-testid={`trust-email-${q.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-[#2c8642] border border-[#5BB247]/40 bg-[#5BB247]/5 px-1.5 py-0.5">
                              <MailCheck size={9} /> Email verified
                            </span>
                          )}
                          {q.driver?.verified && (
                            <span data-testid={`trust-docs-${q.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-[#2c8642] border border-[#5BB247]/40 bg-[#5BB247]/5 px-1.5 py-0.5">
                              <ShieldCheck size={9} /> Documents verified
                            </span>
                          )}
                          {q.driver?.bank_connected && (
                            <span data-testid={`trust-bank-${q.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-[#2c8642] border border-[#5BB247]/40 bg-[#5BB247]/5 px-1.5 py-0.5">
                              <Lock size={9} /> Bank connected
                            </span>
                          )}
                          {q.driver?.subscription_tier === "pro" && (
                            <span data-testid={`trust-pro-${q.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-white bg-[#5BB247] border border-[#5BB247] px-1.5 py-0.5">
                              <Sparkles size={9} /> Pro
                            </span>
                          )}
                        </div>
                        {q.message && <p className="mt-2 text-sm text-[#475569]">{q.message}</p>}
                      </div>
                      <p className="font-display text-2xl font-black tracking-tighter text-[#1E4D55]">{gbp(q.amount_pence)}</p>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <StatusPill status={q.status === "accepted" ? "accepted" : "open"} />
                      {q.status === "pending" && ["open", "quoted"].includes(data.job.status) && (
                        <button
                          onClick={() => acceptQuote(q.id)}
                          disabled={acting}
                          data-testid={`job-accept-quote-${q.id}`}
                          className="inline-flex items-center gap-1.5 bg-[#5BB247] text-white hover:bg-[#4A9939] transition-colors px-4 py-2 text-xs font-semibold disabled:opacity-60"
                        >
                          {acting ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                          Accept this quote
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* v18 — Priced (no payment yet) */}
            {data.job.status === "priced" && (
              <div data-testid="job-priced-banner" className="mt-6 bg-[#F5A623]/5 border border-[#F5A623]/40 p-5">
                <div className="flex items-start gap-3">
                  <PoundSterling size={20} className="text-[#A66800] shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-display text-lg font-bold tracking-tight text-[#0A0A0A]">
                      Pay {gbp(data.job.amount_pence)} to publish this job
                    </p>
                    <p className="mt-1 text-sm text-[#475569]">
                      Funds sit safely in Stripe escrow until you confirm the job is done. We&apos;ll only show this booking to verified drivers <em>after</em> you pay.
                    </p>
                    <button
                      onClick={() => setShowCheckout(true)}
                      data-testid="job-priced-pay-button"
                      className="mt-4 inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3 text-sm font-semibold transition-colors"
                    >
                      <Lock size={14} />
                      Pay {gbp(data.job.amount_pence)} into escrow
                    </button>
                  </div>
                </div>
                {data.job.pricing_breakdown && (
                  <div className="mt-4 pt-4 border-t border-[#F5A623]/30 space-y-1 text-xs text-[#475569]">
                    {data.job.pricing_breakdown.map((b, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span>{b.label}</span>
                        <span className="font-mono text-[#0A0A0A]">{gbp(b.amount_pence)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* v18 — Awaiting driver (paid, looking for driver) */}
            {data.job.status === "awaiting_driver" && (
              <div data-testid="job-awaiting-driver-banner" className="mt-6 bg-[#1E4D55]/5 border border-[#1E4D55]/40 p-5">
                <div className="flex items-start gap-3">
                  <Truck size={20} className="text-[#1E4D55] shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-display text-lg font-bold tracking-tight text-[#0A0A0A]">
                      Funds locked — finding you a driver
                    </p>
                    <p className="mt-1 text-sm text-[#475569]">
                      Your {gbp(data.job.amount_pence)} is safely in escrow. Verified Raanah drivers can now accept this booking. You&apos;ll get a WhatsApp ping the moment someone picks it up.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* v18.5 — Live timer + overtime panel (shows for in_progress / ready / completed) */}
            {data.job.auto_priced && data.job.timer && (
              <JobTimerPanel timer={data.job.timer} audience="customer" />
            )}

            {/* v20 — Clock-in / clock-out proof photos (customer can verify) */}
            {(data.job.clock_in_photo_url || data.job.clock_out_photo_url) && (
              <div data-testid="job-clock-proofs" className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {data.job.clock_in_photo_url && (
                  <a href={data.job.clock_in_photo_url} target="_blank" rel="noopener noreferrer" className="block bg-white border border-[#0A0A0A]/10 overflow-hidden hover:border-[#1E4D55] transition-colors">
                    <img src={data.job.clock_in_photo_url} alt="driver clock-in" className="w-full aspect-video object-cover" />
                    <div className="p-3">
                      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#1E4D55]">Driver clocked in</p>
                      <p className="mt-1 text-xs text-[#475569]">
                        {new Date(data.job.clock_in_at).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}
                      </p>
                    </div>
                  </a>
                )}
                {data.job.clock_out_photo_url && (
                  <a href={data.job.clock_out_photo_url} target="_blank" rel="noopener noreferrer" className="block bg-white border border-[#0A0A0A]/10 overflow-hidden hover:border-[#5BB247] transition-colors">
                    <img src={data.job.clock_out_photo_url} alt="driver clock-out" className="w-full aspect-video object-cover" />
                    <div className="p-3">
                      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#2c8642]">Driver clocked out</p>
                      <p className="mt-1 text-xs text-[#475569]">
                        {new Date(data.job.clock_out_at).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}
                      </p>
                    </div>
                  </a>
                )}
              </div>
            )}

            {/* Escrow-paid banner */}
            {data.job.status === "paid" && (
              <div data-testid="job-escrow-paid-banner" className="mt-6 bg-[#5BB247]/5 border border-[#5BB247]/40 p-4 flex items-start gap-3">
                <ShieldCheck size={20} className="text-[#2c8642] shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[#0A0A0A]">
                    {data.job.payment_mode === "deposit"
                      ? `Deposit locked in escrow (${gbp(data.job.deposit_pence || data.job.amount_pence)})`
                      : `Funds locked in escrow (${gbp(data.job.amount_pence)})`}
                  </p>
                  <p className="mt-1 text-xs text-[#475569]">
                    {data.job.payment_mode === "deposit"
                      ? `Balance of ${gbp(data.job.balance_pence)} is due on completion. Your driver has been notified and will mark the job in-progress shortly.`
                      : "Your driver has been notified and will mark the job in-progress shortly. Funds release only after you confirm completion."}
                  </p>
                </div>
              </div>
            )}

            {/* v23.11 — Deposit paid · balance still owed (in-progress) */}
            {["in_progress", "ready_for_completion"].includes(data.job.status) &&
              data.job.payment_mode === "deposit" &&
              !data.job.balance_paid && (
                <div data-testid="job-balance-due-banner" className="mt-6 bg-[#F5A623]/5 border border-[#F5A623]/40 p-5">
                  <div className="flex items-start gap-3">
                    <PoundSterling size={20} className="text-[#A66800] shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-display text-lg font-bold tracking-tight text-[#0A0A0A]">
                        Balance of {gbp(data.job.balance_pence)} owed
                      </p>
                      <p className="mt-1 text-sm text-[#475569]">
                        You paid a {gbp(data.job.deposit_pence)} deposit upfront. Settle the remaining {gbp(data.job.balance_pence)} to release the job for completion.
                      </p>
                      <button
                        onClick={() => setShowBalanceCheckout(true)}
                        data-testid="job-pay-balance-button"
                        className="mt-4 inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3 text-sm font-semibold transition-colors"
                      >
                        <Lock size={14} />
                        Pay balance {gbp(data.job.balance_pence)} now
                      </button>
                    </div>
                  </div>
                </div>
              )}

            {/* Pay-to-confirm CTA on accepted-but-unpaid */}
            {data.job.status === "accepted" && (
              <div data-testid="job-pay-prompt" className="mt-6 bg-[#F5A623]/5 border border-[#F5A623]/40 p-5">
                <div className="flex items-start gap-3">
                  <Lock size={20} className="text-[#A66800] shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-display text-lg font-bold tracking-tight text-[#0A0A0A]">
                      Pay {gbp(data.job.amount_pence)} to lock in your driver
                    </p>
                    <p className="mt-1 text-sm text-[#475569]">
                      Funds are held safely by Stripe — released only once you confirm the job is done. Cancel any time before the driver starts for a full refund.
                    </p>
                    <button
                      onClick={() => setShowCheckout(true)}
                      data-testid="job-pay-button"
                      className="mt-4 inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3 text-sm font-semibold transition-colors"
                    >
                      <Lock size={14} />
                      Pay {gbp(data.job.amount_pence)} into escrow
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Driver marked job ready — customer confirms */}
            {data.job.status === "ready_for_completion" && !(data.job.payment_mode === "deposit" && !data.job.balance_paid) && (
              <div data-testid="job-confirm-complete-prompt" className="mt-6 bg-[#5BB247]/5 border border-[#5BB247]/40 p-5">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-[#2c8642] shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-display text-lg font-bold tracking-tight text-[#0A0A0A]">
                      Your driver says the job is done
                    </p>
                    <p className="mt-1 text-sm text-[#475569]">
                      Confirm to release {gbp((data.job.amount_pence || 0) - (data.job.platform_fee_pence || 0))} to the driver. Raanah keeps {gbp(data.job.platform_fee_pence)} as our platform fee.
                    </p>
                    <button
                      onClick={confirmComplete}
                      disabled={acting}
                      data-testid="job-confirm-complete-button"
                      className="mt-4 inline-flex items-center gap-2 bg-[#5BB247] hover:bg-[#4A9939] text-white px-5 py-3 text-sm font-semibold transition-colors disabled:opacity-60"
                    >
                      {acting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                      Confirm completion & release funds
                    </button>
                  </div>
                </div>
              </div>
            )}

            {data.job.status === "completed" && (
              <div data-testid="job-completed-banner" className="mt-6 bg-[#5BB247]/5 border border-[#5BB247]/40 p-4 inline-flex items-center gap-2">
                <CheckCircle2 size={16} className="text-[#2c8642]" />
                <span className="text-sm text-[#0A0A0A] font-semibold">Job completed — {gbp(data.job.amount_pence)} fully paid out.</span>
              </div>
            )}

            {["open", "quoted", "priced", "awaiting_driver", "accepted", "paid"].includes(data.job.status) && (
              <div className="mt-8 pt-6 border-t border-[#0A0A0A]/10 flex items-center justify-end">
                <button
                  onClick={cancelJob}
                  disabled={acting}
                  data-testid="job-cancel-button"
                  className="text-xs font-semibold text-[#B91C1C] hover:underline disabled:opacity-60"
                >
                  Cancel this job{["paid", "awaiting_driver"].includes(data.job.status) ? " (full refund)" : ""}
                </button>
              </div>
            )}
          </>
        )}
      </motion.div>

      {showCheckout && (
        <CheckoutModal
          jobId={jobId}
          onClose={() => setShowCheckout(false)}
          onPaid={async () => {
            setShowCheckout(false);
            await load();
            if (onChanged) await onChanged();
          }}
        />
      )}

      {showBalanceCheckout && (
        <BalanceCheckoutModal
          jobId={jobId}
          onClose={() => setShowBalanceCheckout(false)}
          onPaid={async () => {
            setShowBalanceCheckout(false);
            await load();
            if (onChanged) await onChanged();
          }}
        />
      )}

      {showRating && data?.job?.status === "completed" && (
        <RatingModal
          endpoint={`/customer/jobs/${jobId}/rate-driver`}
          title="How was your driver?"
          subtitle="Your rating helps other customers pick the right driver."
          ratee_name={data.quotes?.find((q) => q.status === "accepted")?.driver?.name}
          onClose={() => setShowRating(false)}
        />
      )}
    </div>
  );
}

/* ---------------- Checkout Modal (Stripe PaymentElement) ---------------- */

let _stripePromise = null;
function getStripePromise(pk) {
  if (!_stripePromise && pk) _stripePromise = loadStripe(pk);
  return _stripePromise;
}

function CheckoutModal({ jobId, onClose, onPaid }) {
  const [clientSecret, setClientSecret] = React.useState("");
  const [pk, setPk] = React.useState("");
  const [amountPence, setAmountPence] = React.useState(0);
  const [balancePence, setBalancePence] = React.useState(0);
  const [fullAmountPence, setFullAmountPence] = React.useState(0);
  const [mode, setMode] = React.useState("deposit"); // v23.11 — default to deposit (lower barrier)
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setClientSecret("");
    (async () => {
      try {
        const { data } = await apiClient.post(`/customer/jobs/${jobId}/pay`, { mode });
        if (cancelled) return;
        setClientSecret(data.client_secret);
        setPk(data.publishable_key);
        setAmountPence(data.amount_pence);
        setBalancePence(data.balance_pence || 0);
        setFullAmountPence(data.full_amount_pence || data.amount_pence);
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't initialise payment");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, mode]);

  return (
    <div data-testid="checkout-modal" className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white border border-[#0A0A0A]/10 max-w-lg w-full p-8 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">Secure escrow</p>
            <h3 className="font-display mt-2 text-2xl font-bold tracking-tight text-[#0A0A0A]">
              {amountPence ? `Pay ${gbp(amountPence)}` : "Pay into escrow"}
            </h3>
          </div>
          <button onClick={onClose} data-testid="checkout-close" className="p-2 hover:bg-[#F5F5F5]"><X size={18} /></button>
        </div>

        {/* v23.11 — Pay-in-full vs deposit toggle */}
        {fullAmountPence > 0 && (
          <div className="mt-5 grid grid-cols-2 gap-2" data-testid="checkout-mode-toggle">
            <button
              type="button"
              onClick={() => setMode("full")}
              data-testid="checkout-mode-full"
              className={`p-3 text-left border-2 transition-colors ${mode === "full" ? "border-[#1E4D55] bg-[#1E4D55]/5" : "border-[#0A0A0A]/10 hover:border-[#1E4D55]/40"}`}
            >
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#475569]">Pay in full now</p>
              <p className="font-display text-lg font-black text-[#0A0A0A] mt-1">{gbp(fullAmountPence)}</p>
              <p className="text-[11px] text-[#475569] mt-0.5">Held in escrow until job done</p>
            </button>
            <button
              type="button"
              onClick={() => setMode("deposit")}
              data-testid="checkout-mode-deposit"
              className={`p-3 text-left border-2 transition-colors ${mode === "deposit" ? "border-[#1E4D55] bg-[#1E4D55]/5" : "border-[#0A0A0A]/10 hover:border-[#1E4D55]/40"}`}
            >
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#475569]">Pay 30% deposit</p>
              <p className="font-display text-lg font-black text-[#0A0A0A] mt-1">{gbp(Math.round(fullAmountPence * 0.30))}</p>
              <p className="text-[11px] text-[#475569] mt-0.5">Balance {gbp(fullAmountPence - Math.round(fullAmountPence * 0.30))} on completion</p>
            </button>
          </div>
        )}

        {loading && (
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#475569]"><Loader2 size={14} className="animate-spin" /> Setting up secure checkout…</div>
        )}

        {error && (
          <div data-testid="checkout-error" className="mt-4 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!loading && clientSecret && pk && (
          <Elements stripe={getStripePromise(pk)} options={{ clientSecret, appearance: { theme: "stripe", variables: { colorPrimary: "#1E4D55" } } }}>
            <CheckoutForm onPaid={onPaid} amountPence={amountPence} />
          </Elements>
        )}

        <p className="mt-6 text-[11px] text-[#475569] inline-flex items-center gap-1">
          <Lock size={11} /> Powered by Stripe. Card details never touch Raanah&apos;s servers.
        </p>
      </motion.div>
    </div>
  );
}

function CheckoutForm({ onPaid, amountPence }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError("");
    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });
    if (stripeError) {
      setError(stripeError.message || "Payment failed");
      setSubmitting(false);
      return;
    }
    // Webhook will flip job → paid asynchronously; we still tell the parent to refetch.
    onPaid();
  };

  return (
    <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="checkout-form">
      <PaymentElement />
      {error && (
        <div data-testid="checkout-card-error" className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || submitting}
        data-testid="checkout-pay-button"
        className="w-full inline-flex items-center justify-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3 font-semibold transition-colors disabled:opacity-60"
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
        {submitting ? "Securing payment…" : `Pay ${gbp(amountPence)} now`}
      </button>
    </form>
  );
}


/* ---------------- Balance Checkout Modal (v23.11) ---------------- */
function BalanceCheckoutModal({ jobId, onClose, onPaid }) {
  const [clientSecret, setClientSecret] = React.useState("");
  const [pk, setPk] = React.useState("");
  const [amountPence, setAmountPence] = React.useState(0);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const { data } = await apiClient.post(`/customer/jobs/${jobId}/pay-balance`);
        if (cancelled) return;
        setClientSecret(data.client_secret);
        setPk(data.publishable_key);
        setAmountPence(data.amount_pence);
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't initialise balance payment");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  return (
    <div data-testid="balance-checkout-modal" className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white border border-[#0A0A0A]/10 max-w-lg w-full p-8 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">Balance payment</p>
            <h3 className="font-display mt-2 text-2xl font-bold tracking-tight text-[#0A0A0A]">
              {amountPence ? `Pay balance ${gbp(amountPence)}` : "Settle remaining balance"}
            </h3>
            <p className="mt-2 text-xs text-[#475569]">Final payment to release the job for completion.</p>
          </div>
          <button onClick={onClose} data-testid="balance-checkout-close" className="p-2 hover:bg-[#F5F5F5]"><X size={18} /></button>
        </div>

        {loading && (
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#475569]"><Loader2 size={14} className="animate-spin" /> Setting up checkout…</div>
        )}

        {error && (
          <div data-testid="balance-checkout-error" className="mt-4 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!loading && clientSecret && pk && (
          <Elements stripe={getStripePromise(pk)} options={{ clientSecret, appearance: { theme: "stripe", variables: { colorPrimary: "#1E4D55" } } }}>
            <CheckoutForm onPaid={onPaid} amountPence={amountPence} />
          </Elements>
        )}

        <p className="mt-6 text-[11px] text-[#475569] inline-flex items-center gap-1">
          <Lock size={11} /> Powered by Stripe.
        </p>
      </motion.div>
    </div>
  );
}
